import { CORE_URL } from "@/constants/api";
import { DB_FILTER, DB_ORDER, DB_ORG, DB_PATH } from "@/constants/db";
import { VITE_CORE_API_URL } from "@/constants/env";
import type { CompleteDashboardData } from "@/models/organization/dashboard-analytics/dashboard-analytics.model";
import { auth } from "@/services/auth-service";
import { db } from "@/services/auth-service";
import { selfOrganizationAssetTypes } from "@/services/organization-service";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type {
	DocumentReference,
	DocumentSnapshot,
	QueryConstraint,
	QuerySnapshot,
} from "firebase/firestore";
import {
	type OrderByDirection,
	addDoc,
	collection,
	doc,
	getCountFromServer,
	getDoc,
	getDocs,
	orderBy,
	query,
	setDoc,
	updateDoc,
	where,
} from "firebase/firestore";

dayjs.extend(customParseFormat);

/**
 * Converts json to b64, used for sending small amounts of json
 * @param json - The JSON object to convert
 * @returns {string | null} Base64 encoded string or null if conversion fails
 */
const jsonToBase64 = (json: unknown): string | null => {
	try {
		return btoa(encodeURIComponent(JSON.stringify(json)));
	} catch {
		return null;
	}
};

export const formatDateDynamic = (date: unknown): string | null => {
	if (!date) {
		return null;
	}

	// Define common date formats
	const formats = [
		"MMMM D, YYYY [at] h:mm:ss A [UTC]Z",
		"YYYY-MM-DDTHH:mm:ssZ",
		"YYYY-MM-DDTHH:mm:ss.SSSZ",
		"MMM D, YYYY",
		"MM/DD/YYYY",
		"YYYY-MM-DD",
		"DD-MM-YYYY",
		"D MMMM YYYY",
		"D MMM YYYY",
		"YYYY/MM/DD",
		"DD/MM/YYYY",
		"MMM D, YYYY, h:mm:ss A",
		"MMM D, YYYY, h:mm A",
	];

	let parsedDate: dayjs.Dayjs | null = null;
	for (const format of formats) {
		parsedDate = dayjs(String(date), format, true);
		if (parsedDate.isValid()) {
			break;
		}
	}

	if (!parsedDate || !parsedDate.isValid()) {
		return null;
	}

	return parsedDate.format("MM/DD/YYYY");
};

////////////////////////////////////////////////////////////
// Asset Aggregate
////////////////////////////////////////////////////////////

export const getAssetAggregate = async (
	organizationId: number,
): Promise<CompleteDashboardData> => {
	try {
		const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${organizationId}/aggregate`;

		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(
				`Failed to fetch asset aggregate: ${errorData.detail || response.statusText}`,
			);
		}

		const data = await response.json();

		return data;
	} catch (error) {
		console.error("Error fetching asset aggregate:", error);
		return {
			assets: [],
			analytics: {
				healthScore: 0,
				totalBudget: 0,
				dollarAmountPastUsefulLife: 0,
			},
			locations: [],
		};
	}
};

// TODO: Revist post migration
export const getMasterAssetsTypes = async (): Promise<QuerySnapshot> => {
	try {
		const assetTypes = await getDocs(
			query(
				collection(db, DB_PATH.ASSET_TYPES),
				orderBy(DB_FILTER.NAME, DB_ORDER.ASC as OrderByDirection),
			),
		);
		return assetTypes;
	} catch (err) {
		throw new Error(err instanceof Error ? err.message : String(err));
	}
};

export const isMasterAssetType = async (name: string): Promise<boolean> => {
	try {
		if (!auth.currentUser) {
			throw new Error("User not authenticated");
		}

		const url = `${
			import.meta.env.VITE_ALFRED_SERVICE_URL
		}/master_asset_type/lookup/${name}`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to get master asset type: ${name} ${errorDetails.detail}`,
			);
		}

		const responsejson = await response.json();
		return responsejson !== null;
	} catch (exception) {
		throw new Error(
			exception instanceof Error ? exception.message : String(exception),
		);
	}
};

// TODO: Revist post migration
export const addMasterAssetTypes = async (
	data: Record<string, unknown>,
): Promise<DocumentSnapshot> => {
	try {
		const ref = await addDoc(collection(db, DB_PATH.ASSET_TYPES), data);
		const docSnap = await getDoc(ref);
		return docSnap;
	} catch (err) {
		throw new Error(err instanceof Error ? err.message : String(err));
	}
};

// TODO: Revist post migration
export const savePotentialMasterAsset = async (
	data: Record<string, unknown>,
): Promise<DocumentSnapshot> => {
	try {
		const ref = await addDoc(
			collection(db, DB_PATH.MASTER_ASSETS_FOR_REVIEW),
			data,
		);
		const docSnap = await getDoc(ref);
		return docSnap;
	} catch (err) {
		throw new Error(err instanceof Error ? err.message : String(err));
	}
};

// TODO: Revist post migration
export const getAssetTypeFromRef = async (
	assetTypeRef: DocumentReference,
): Promise<DocumentSnapshot> => {
	try {
		const assetType = await getDoc(assetTypeRef);
		return assetType;
	} catch (err) {
		throw new Error(err instanceof Error ? err.message : String(err));
	}
};

// TODO: Revist post migration
export const getAssetTypeCategories = async (
	organization: string,
	assetName?: string,
): Promise<QuerySnapshot> => {
	try {
		const colRef = collection(
			db,
			DB_PATH.ORGANIZATIONS,
			organization,
			DB_PATH.ASSET_TYPE_CATEGORIES,
		);

		const assetTypeCategory = assetName
			? await getDocs(query(colRef, where(DB_FILTER.NAME, "==", assetName)))
			: await getDocs(
					query(
						colRef,
						orderBy(DB_FILTER.NAME, DB_ORDER.ASC as OrderByDirection),
					),
				);
		return assetTypeCategory;
	} catch (err) {
		throw new Error(err instanceof Error ? err.message : String(err));
	}
};

// TODO: Revist post migration
export const getAssetTypes = async (
	organization: string,
	assetName?: string,
): Promise<QuerySnapshot> => {
	try {
		const colRef = collection(
			db,
			DB_PATH.ORGANIZATIONS,
			organization,
			DB_PATH.ASSET_TYPES,
		);

		const assetTypes = assetName
			? await getDocs(query(colRef, where(DB_FILTER.NAME, "==", assetName)))
			: await getDocs(
					query(
						colRef,
						orderBy(DB_FILTER.NAME, DB_ORDER.ASC as OrderByDirection),
					),
				);
		return assetTypes;
	} catch (err) {
		throw new Error(err instanceof Error ? err.message : String(err));
	}
};

export const getSelfOrganizationAssetTypes = async () => {
	try {
		const assetTypes = await selfOrganizationAssetTypes();
		return assetTypes;
	} catch (err) {
		throw new Error(err instanceof Error ? err.message : String(err));
	}
};

export const getAssetTypesForForm = async (
	organization: string,
	setState: (state: Record<string, unknown>) => void,
): Promise<void> => {
	const assetTypes = await selfOrganizationAssetTypes(true);
	const parsed = assetTypes.reduce(
		(acc: Record<string, unknown>, item: { id: number }) => {
			acc[item.id] = item;
			return acc;
		},
		{},
	);
	setState(parsed);
};

// TODO: Revist post migration
export const addAssetType = async (
	path: string,
	isAll: boolean,
	data: Record<string, unknown>,
): Promise<DocumentSnapshot> => {
	try {
		const colRef = isAll
			? collection(db, DB_PATH.ORGANIZATIONS, path, DB_PATH.ASSET_TYPES)
			: collection(db, path);
		const ref = await addDoc(colRef, data);
		const docSnap = await getDoc(ref);
		return docSnap;
	} catch (err) {
		throw new Error(err instanceof Error ? err.message : String(err));
	}
};

// TODO: Revist post migration
export const getFormAssetTypes = async (
	organization: string,
	form: string,
	type?: string,
): Promise<QuerySnapshot> => {
	let docQuery = query(
		collection(
			db,
			DB_PATH.ORGANIZATIONS,
			organization,
			DB_PATH.FORMS,
			form,
			DB_PATH.ASSET_TYPES,
		),
	);
	if (type) {
		docQuery = query(docQuery, where(DB_FILTER.NAME, "==", type));
	}
	const assetTypes = await getDocs(docQuery);
	return assetTypes;
};

// TODO: Revist post migration
export const getAssets = async (
	organization: string,
	locationId?: string,
	type?: string,
	tag?: string,
	order?: string,
): Promise<QuerySnapshot> => {
	console.log("Inside getAssets", organization, locationId, type, tag, order);
	const params: QueryConstraint[] = [where(DB_FILTER.DELETED, "==", false)];
	if (locationId) {
		params.push(where(DB_FILTER.OTHER_LOCATION_ID, "==", locationId));
	}
	if (type) {
		params.push(where(DB_FILTER.TYPE, "==", type));
	}
	if (tag) {
		params.push(where(DB_FILTER.OTHER_ASSET_TAG, "==", tag));
	}
	if (order) {
		params.push(orderBy(order, DB_ORDER.ASC as OrderByDirection));
	}
	const assetsSnap = await getDocs(
		query(
			collection(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.ASSETS),
			...params,
		),
	);
	return assetsSnap;
};

export const getAssetLocations = async (
	locationId: string | number,
): Promise<unknown> => {
	try {
		if (!auth.currentUser) {
			throw new Error("User not authenticated");
		}

		const url = `${
			import.meta.env.VITE_ALFRED_SERVICE_URL
		}/location/${locationId}/asset_locations`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to get asset map positions for location: ${locationId} ${errorDetails.detail}`,
			);
		}

		const responsejson = await response.json();
		return responsejson;
	} catch (exception) {
		throw new Error(
			exception instanceof Error ? exception.message : String(exception),
		);
	}
};

export const getAsset = async (id: string | number): Promise<unknown> => {
	try {
		if (!auth.currentUser) {
			throw new Error("User not authenticated");
		}

		const url = `${import.meta.env.VITE_ALFRED_SERVICE_URL}/asset/lookup/${id}`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(`Failed to get asset: ${id} ${errorDetails.detail}`);
		}

		const responsejson = await response.json();
		return responsejson;
	} catch (exception) {
		throw new Error(
			exception instanceof Error ? exception.message : String(exception),
		);
	}
};

// TODO: Revist post migration
export const getLocationAssets = async (
	organization: string,
	locationid?: string,
): Promise<QuerySnapshot> => {
	const params: QueryConstraint[] = [where(DB_FILTER.DELETED, "==", false)];
	if (locationid) {
		params.push(where(DB_FILTER.OTHER_LOCATION_ID, "==", locationid));
	}
	const assetsSnap = await getDocs(
		query(
			collection(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.ASSETS),
			...params,
		),
	);
	return assetsSnap;
};

// TODO: Revist post migration
export const getAssetFromParentRef = async (
	assetId: string,
): Promise<{ path: string; data: unknown }> => {
	const docRef = doc(db, DB_PATH.ASSETS, assetId);
	const docSnap = await getDoc(docRef);
	return { path: assetId, data: docSnap.data() };
};

// TODO: Revist post migration
export const addAsset = async (
	organization: string,
	data: Record<string, unknown>,
): Promise<DocumentReference> => {
	const ref = await addDoc(
		collection(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.ASSETS),
		data,
	);
	return ref;
};

// TODO: Revist post migration
export const addAssetVersionHistory = async (
	organization: string,
	id: string,
	data: Record<string, unknown>,
): Promise<void> => {
	await addDoc(
		collection(
			db,
			DB_PATH.ORGANIZATIONS,
			organization,
			DB_PATH.ASSETS,
			id,
			DB_PATH.VERSION_HISTORY,
		),
		data,
	);
};

// TODO: Revist post migration - Legacy Firebase update function (deprecated)
export const updateAssetLegacy = async (
	organization: string,
	id: string,
	updatedAsset: Record<string, unknown>,
): Promise<void> => {
	await setDoc(
		doc(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.ASSETS, id),
		updatedAsset,
	);
};

// TODO: Revist post migration - Legacy Firebase delete function (deprecated)
export const deleteAssetLegacy = async (
	assetRef: DocumentReference,
): Promise<void> => {
	const asset = await getDoc(assetRef);
	const assetData = asset.data();
	if (!assetData) {
		throw new Error("Asset data not found");
	}
	const submissionRef = assetData.responseRef as DocumentReference;
	await updateDoc(assetRef, { deleted: true });
	if (submissionRef) {
		await updateDoc(submissionRef, { deleted: true });
	}
};

/**
 * Deletes an asset using the Core API
 * This function soft deletes an asset by setting is_deleted = CURRENT_TIMESTAMP and deleted_by = userId.
 * The asset is not permanently removed from the database, but will be excluded from normal queries.
 *
 * @param {number | string} asset_id - The ID of the asset to delete
 * @param {string} [deletionReason] - Optional deletion reason (e.g., "Removed", "Shelved", "Error")
 * @param {string} [deletionNotes] - Optional additional notes for the deletion
 * @returns {Promise<{ deleted: boolean; asset_id: number }>} Deletion confirmation
 * @throws {Error} If the deletion fails
 */
export const deleteAsset = async (
	asset_id: number | string,
	deletionReason?: string,
	deletionNotes?: string,
): Promise<{ deleted: boolean; asset_id: number }> => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${asset_id}`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const body: { deletionReason?: string; deletionNotes?: string } = {};
		if (deletionReason) {
			body.deletionReason = deletionReason;
		}
		if (deletionNotes) {
			body.deletionNotes = deletionNotes;
		}

		const response = await fetch(url, {
			method: "DELETE",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message ||
					`Failed to delete asset: HTTP ${response.status}`,
			);
		}

		const result = await response.json();
		return result.data;
	} catch (exception: unknown) {
		console.error("Failed to delete asset:", exception);
		const errorMessage =
			exception instanceof Error ? exception.message : String(exception);
		throw new Error(`Failed to delete asset: ${errorMessage}`);
	}
};

/**
 * Undeletes an asset using the Core API
 * This function restores a soft-deleted asset by setting is_deleted = NULL and deleted_by = NULL.
 * The asset will be restored and will appear in normal queries again.
 *
 * @param {number | string} asset_id - The ID of the asset to undelete
 * @returns {Promise<{ undeleted: boolean; asset_id: number }>} Undeletion confirmation
 * @throws {Error} If the undeletion fails
 */
export const undeleteAsset = async (
	asset_id: number | string,
): Promise<{ undeleted: boolean; asset_id: number }> => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${asset_id}/undelete`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "PATCH",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message ||
					`Failed to undelete asset: HTTP ${response.status}`,
			);
		}

		const result = await response.json();
		return result.data;
	} catch (exception: unknown) {
		console.error("Failed to undelete asset:", exception);
		const errorMessage =
			exception instanceof Error ? exception.message : String(exception);
		throw new Error(`Failed to undelete asset: ${errorMessage}`);
	}
};

/**
 * Interface for updating an asset
 * Only includes fields that should be editable
 */
export interface UpdateAssetRequest {
	assetTag?: string;
	organizationAssetTypeId?: number;
	condition?: string;
	dateOfBirth?: string;
	isDobEstimate?: boolean;
	generalNotes?: string;
	manufacturerId?: number;
	modelNumber?: string;
	serialNumber?: string;
	manufacturerDate?: string;
	tempAlertTag?: string;
	powerSource?: string;
	organizationEquipmentAreaId?: number;
	organizationEquipmentSubAreaId?: number;
	uniqueFields?: Record<string, any>;
	warranty?: {
		hasWarranty?: boolean;
		isWarrantyExpired?: boolean;
		isNotFound?: boolean;
		startDate?: string;
		expirationDate?: string;
		type?: string;
		term?: number;
		provider?: string;
		scProviderId?: number;
	};
}

/**
 * Interface for asset revision history entry
 */
export interface AssetRevision {
	id: number;
	assetId: number;
	userId: number;
	organizationId: number;
	fieldName: string;
	oldValue: string | number | boolean | null;
	newValue: string | number | boolean | null;
	oldValueDisplay?: string;
	newValueDisplay?: string;
	revisionTimestamp: string;
	userName?: string;
	userEmail?: string;
}

/**
 * Updates an asset using the Core API
 * This function updates asset fields and tracks all changes in revision history.
 *
 * @param {number | string} asset_id - The ID of the asset to update
 * @param {UpdateAssetRequest} updateData - The fields to update
 * @returns {Promise<any>} Updated asset aggregate
 * @throws {Error} If the update fails
 */
export const updateAsset = async (
	asset_id: number | string,
	updateData: UpdateAssetRequest,
): Promise<any> => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${asset_id}`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "PUT",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(updateData),
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message ||
					`Failed to update asset: HTTP ${response.status}`,
			);
		}

		const result = await response.json();
		return result.data;
	} catch (exception: unknown) {
		// Re-throw the error as-is to preserve the specific error message from the API
		if (exception instanceof Error) {
			throw exception;
		}
		throw new Error(String(exception));
	}
};

/**
 * Gets revision history for an asset using the Core API
 *
 * @param {number | string} asset_id - The ID of the asset
 * @returns {Promise<AssetRevision[]>} Array of revision history entries
 * @throws {Error} If fetching revision history fails
 */
export const getAssetRevisionHistory = async (
	asset_id: number | string,
): Promise<AssetRevision[]> => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${asset_id}/revisions`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message ||
					`Failed to fetch revision history: HTTP ${response.status}`,
			);
		}

		const result = await response.json();
		return result.data;
	} catch (exception: unknown) {
		console.error("Failed to fetch revision history:", exception);
		const errorMessage =
			exception instanceof Error ? exception.message : String(exception);
		throw new Error(`Failed to fetch revision history: ${errorMessage}`);
	}
};

/**
 * Adds a photo to an asset using the Core API
 *
 * @param {number | string} asset_id - The ID of the asset
 * @param {string} url - The URL of the photo
 * @param {string} type - The type of photo (e.g., 'ASSET_TAG', 'MANUFACTURER_PLATE', 'OVERVIEW')
 * @returns {Promise<{id: number, assetId: number, type: string, url: string}>} The created photo record
 * @throws {Error} If adding the photo fails
 */
export const addAssetPhoto = async (
	asset_id: number | string,
	url: string,
	type: string,
): Promise<{ id: number; assetId: number; type: string; url: string }> => {
	const apiUrl = `${VITE_CORE_API_URL}${CORE_URL}/asset/${asset_id}/photos`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(apiUrl, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ url, type }),
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message || `Failed to add photo: HTTP ${response.status}`,
			);
		}

		const result = await response.json();
		return result.data;
	} catch (exception: unknown) {
		console.error("Failed to add photo:", exception);
		const errorMessage =
			exception instanceof Error ? exception.message : String(exception);
		throw new Error(`Failed to add photo: ${errorMessage}`);
	}
};

/**
 * Deletes a photo from an asset using the Core API
 *
 * @param {number | string} asset_id - The ID of the asset
 * @param {number | string} photo_id - The ID of the photo to delete
 * @returns {Promise<boolean>} True if deleted successfully
 * @throws {Error} If deleting the photo fails
 */
export const deleteAssetPhoto = async (
	asset_id: number | string,
	photo_id: number | string,
): Promise<boolean> => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${asset_id}/photos/${photo_id}`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "DELETE",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message ||
					`Failed to delete photo: HTTP ${response.status}`,
			);
		}

		return true;
	} catch (exception: unknown) {
		console.error("Failed to delete photo:", exception);
		const errorMessage =
			exception instanceof Error ? exception.message : String(exception);
		throw new Error(`Failed to delete photo: ${errorMessage}`);
	}
};

/**
 * Moves an asset to a different location using the Core API.
 * Records the move reason and optional notes for audit tracking.
 *
 * @param {number | string} assetId - The ID of the asset to move
 * @param {number} toLocationId - The ID of the destination location
 * @param {'error_correction' | 'physical_move'} reason - Reason for the move
 * @param {string} [notes] - Optional notes about the move
 * @returns {Promise<any>} Updated asset aggregate
 * @throws {Error} If the move fails
 */
export const moveAsset = async (
	assetId: number | string,
	toLocationId: number,
	reason: "error_correction" | "physical_move",
	notes?: string,
): Promise<any> => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${assetId}/move`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const body: {
			toLocationId: number;
			reason: string;
			notes?: string;
		} = { toLocationId, reason };
		if (notes) {
			body.notes = notes;
		}

		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message ||
					`Failed to move asset: HTTP ${response.status}`,
			);
		}

		const result = await response.json();
		return result.data;
	} catch (exception: unknown) {
		if (exception instanceof Error) {
			throw exception;
		}
		throw new Error(String(exception));
	}
};

////////////////////////////////////////////////////////////
// Child Assets (hierarchy)
////////////////////////////////////////////////////////////

/**
 * Request payload for creating a child asset under a parent.
 * Location is inherited from the parent; the tag is auto-generated from the
 * parent's tag (e.g. HVAC-001 -> HVAC-001.1) unless provided.
 */
export interface CreateChildAssetRequest {
	organizationAssetTypeId: number;
	assetTag?: string;
	manufacturerId?: number;
	modelNumber?: string;
	serialNumber?: string;
	condition?: string;
	generalNotes?: string;
	dateOfBirth?: string;
	tagType?: "PHYSICAL" | "VIRTUAL";
}

/** Lightweight child asset row returned by GET /asset/:id/children */
export interface ChildAssetRow {
	id: number;
	parentAssetId: number;
	assetTag: string | null;
	tagType: "PHYSICAL" | "VIRTUAL";
	serialNumber: string | null;
	modelNumber: string | null;
	condition: string | null;
	locationId: number;
	organizationAssetTypeId: number | null;
	assetTypeName: string | null;
	manufacturerName: string | null;
	childCount: number;
	createdTimestamp: string;
}

/** Ancestor reference returned by GET /asset/:id/ancestors (root first) */
export interface AssetAncestor {
	id: number;
	assetTag: string | null;
	tagType: "PHYSICAL" | "VIRTUAL";
	assetTypeName: string | null;
}

/**
 * Creates a child asset under a parent using the Core API.
 *
 * @param {number | string} parentAssetId - The ID of the parent asset
 * @param {CreateChildAssetRequest} data - Child asset fields (asset type required)
 * @returns {Promise<any>} The created child asset aggregate
 * @throws {Error} If the creation fails
 */
export const createChildAsset = async (
	parentAssetId: number | string,
	data: CreateChildAssetRequest,
): Promise<any> => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${parentAssetId}/children`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message ||
					`Failed to create child asset: HTTP ${response.status}`,
			);
		}

		const result = await response.json();
		return result.data;
	} catch (exception: unknown) {
		if (exception instanceof Error) {
			throw exception;
		}
		throw new Error(String(exception));
	}
};

/**
 * Lists direct children of an asset using the Core API.
 *
 * @param {number | string} assetId - The ID of the parent asset
 * @returns {Promise<ChildAssetRow[]>} Direct children with their own child counts
 * @throws {Error} If fetching fails
 */
export const getAssetChildren = async (
	assetId: number | string,
): Promise<ChildAssetRow[]> => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${assetId}/children`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message ||
					`Failed to fetch child assets: HTTP ${response.status}`,
			);
		}

		const result = await response.json();
		return result.data;
	} catch (exception: unknown) {
		console.error("Failed to fetch child assets:", exception);
		const errorMessage =
			exception instanceof Error ? exception.message : String(exception);
		throw new Error(`Failed to fetch child assets: ${errorMessage}`);
	}
};

/**
 * Gets the ancestor chain of an asset (root first) for breadcrumb display.
 * Returns an empty array for root (top-level) assets.
 *
 * @param {number | string} assetId - The ID of the asset
 * @returns {Promise<AssetAncestor[]>} Ancestors ordered root-first
 */
export const getAssetAncestors = async (
	assetId: number | string,
): Promise<AssetAncestor[]> => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${assetId}/ancestors`;

	try {
		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorDetails = await response.json();
			throw new Error(
				errorDetails.message ||
					`Failed to fetch ancestors: HTTP ${response.status}`,
			);
		}

		const result = await response.json();
		return result.data;
	} catch (exception: unknown) {
		console.error("Failed to fetch asset ancestors:", exception);
		return [];
	}
};

export const getAssetSpecificDetails = async (asset: {
	organization_asset_type?: {
		conditional_inputs?: {
			inputs?: string[];
		};
	};
	[key: string]: unknown;
}): Promise<Array<{ label: string; value: unknown; type?: string }> | null> => {
	if (!asset) {
		return null;
	}
	const specificDetails: Array<{
		label: string;
		value: unknown;
		type?: string;
	}> = [];

	const conditional_inputs =
		asset.organization_asset_type?.conditional_inputs?.inputs;
	if (!conditional_inputs) return null;

	for (const input of conditional_inputs) {
		try {
			const parsedInput = JSON.parse(input);

			// if input is array, not object
			if (Array.isArray(parsedInput)) {
				const label = parsedInput[0]?.label;
				const key = parsedInput[1]?.key;

				if (!label || !key) continue;

				const specificDetail = asset[key];
				// 'true' means Not Found checkbox was checked for this input
				if (specificDetail == "true" || specificDetail === undefined) {
					continue;
				}
				specificDetails.push({ label, value: specificDetail });
			} else {
				let { label, key, type } = parsedInput as {
					label: string;
					key: string;
					type?: string;
				};
				if (label == "Power Source?") label = "Power Source";
				if (key == "refrigerantCharge") key = "totalCharge";
				if (key == "refrigerant") key = "refType";
				const specificDetail = asset[key];
				if (specificDetail === undefined) continue;
				specificDetails.push({ label, value: specificDetail, type });
			}
		} catch (error) {
			console.error("Error parsing input:", error);
			continue;
		}
	}
	return specificDetails;
};

export const searchAssets = async (
	val: string,
	pageNum: number,
	pageSize: number,
	sortBy: string,
	sortOrder: string,
	location?: string,
	startDate?: string,
	endDate?: string,
	columnFilters?: Record<string, unknown>,
): Promise<{ results: unknown[]; count: number } | undefined> => {
	try {
		console.log(sortBy);
		const params = new URLSearchParams({
			limit: String(pageSize),
			sort: sortBy,
			sort_direction: sortOrder,
			offset: String(pageNum),
			search_value: val,
		});
		if (columnFilters && Object.keys(columnFilters).length > 0) {
			const encoded = jsonToBase64(columnFilters);
			if (encoded) {
				params.append("column_filter", encoded);
			}
		}
		if (startDate) {
			params.append("start_date", startDate);
		}
		if (endDate) {
			params.append("end_date", endDate);
		}
		if (location) {
			params.append("location_id", location);
		}

		const url = `${
			import.meta.env.VITE_ALFRED_SERVICE_URL
		}/asset/search?${params.toString()}`;

		if (!auth.currentUser) {
			throw new Error("User not authenticated");
		}

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(`Failed to search for assets: ${errorDetails.detail}`);
		}

		const responsejson = await response.json();
		if (responsejson.hits) {
			return {
				results: responsejson.hits,
				count: responsejson.total_hits,
			};
		}
	} catch (exception) {
		throw new Error(
			exception instanceof Error ? exception.message : String(exception),
		);
	}
};

// TODO: Revist post migration
export const getAssetCountByDecommissionedStatus = async (
	organization: string,
	locationId?: string,
	decommissioned?: string,
): Promise<number> => {
	const params: QueryConstraint[] = [where(DB_FILTER.DELETED, "==", false)];
	if (locationId) {
		params.push(where(DB_FILTER.OTHER_LOCATION_ID, "==", locationId));
	}
	if (decommissioned) {
		params.push(where(DB_FILTER.TYPE, "==", decommissioned));
	}
	const assetsSnap = await getCountFromServer(
		query(
			collection(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.ASSETS),
			...params,
		),
	);
	return assetsSnap.data().count;
};

// TODO: Revist post migration
export const addAssetFromForm = async (
	organization: string,
	submissionRef: DocumentReference,
	data: Record<string, unknown>,
	chosenAssetType: { ref?: DocumentReference } | DocumentReference,
): Promise<DocumentReference> => {
	// Check warranty date/term if there is warranty type
	let percentWarrantyLeft: string | null = null;
	let isWarrantyExpired: boolean | null = null;
	if (data.warrantyExpiration && data.warrantyTerm) {
		const today = new Date();
		const expDate = new Date(String(data.warrantyExpiration));
		const difference = expDate.getTime() - today.getTime();
		const days = difference / (1000 * 60 * 60 * 24);
		percentWarrantyLeft = (
			(days / (Number(data.warrantyTerm) * 30)) *
			100
		).toFixed(1);
		if (Number(percentWarrantyLeft) > 0) isWarrantyExpired = false;
		else if (Number(percentWarrantyLeft) <= 0) isWarrantyExpired = true;
	}
	// Generate asset data obj
	const asset = {
		responseRef: submissionRef,
		assetType: data.assetType || null,
		assetTypeRef:
			(chosenAssetType as { ref?: DocumentReference }).ref || chosenAssetType,
		make: data.manufacturer || null,
		model: data.modelNumber || null,
		serial: data.serialNumber || null,
		dateOfBirth: data.manufacturerDate || null,
		submittingUser: data.submittingUser,
		locationId: (data.location as { id: string | number })?.id,
		other: data,
		deleted: false,
		decommissioned: false,
		isWarrantyExpired,
		percentWarrantyLeft,
		avgUsefulLifeYears: null,
		avgReplCostUSD: null,
		replacementYear: null,
	};
	// Create asset
	const assetRef = await addAsset(organization, asset);
	return assetRef;
};

/**
 * Takes in form responseData and converts it into the format the backend is expecting
 * @param data - Form response data
 * @param draftId - Draft ID
 * @param organization - Organization identifier
 * @returns {Promise<object>} Parsed asset data
 */
const responseDataToParsedAsset = async (
	data: Record<string, unknown>,
	draftId: string | number,
	organization: string,
): Promise<{
	asset: Record<string, unknown>;
	photos: Array<{ type: string; url: unknown }>;
	raw_input: Record<string, unknown>;
	refrigeration?: Record<string, unknown>;
	electrical?: Record<string, unknown>;
	warranty?: Record<string, unknown>;
}> => {
	//Fields not to add to the unique column
	const keysToExclude = [
		// Refrigeration details
		"refrigerant",
		"refrigerantNotAvailable",
		"refrigerantCharge",
		"refrigerantChargeUnitMeasurement",
		"refrigerantChargeNotAvailable",
		"refUsageType",

		// Electrical details
		"amps",
		"voltage",
		"wattage",
		"ampsNotAvailable",
		"voltageNotAvailable",
		"wattageNotAvailable",

		// Warranty details
		"warrantyStartDate",
		"warrantyExpirationDate",
		"warrantyPeriod",
		"warrantyProvider",

		// Photo details
		"idTagAssetImage",
		"tempAlertIdAssetImage",
		"wideAngleAssetImage",
		"equipmentAssetImage",
		"manufacturersPlateAssetImage",

		// Main asset details
		"location",
		"assetType",
		"formId",
		"manufacturerDate",
		"tempAlertId",
		"tempAlertIdNotAvailable",
		"assetGeneralNotes",
		"manufacturerNotAvailable",
		"manufacturerPlateNotAvailable",
		"manufacturerDateNotFound",
		"modelNumber",
		"modelNotAvailable",
		"serialNumber",
		"serialNotAvailable",
		"assetCondition",
		"assetTag",
		"powerSource",
		"assetLocation",
		"externalFirebaseId",
		"assetTypeCategory",

		//Others
		"beginDate",
		"manufacturer",
		"locationId",
		"assetTypeSuggestions",
		"deleted",
		"audited",
		"assetTypeRef",
	];
	const uniques = Object.keys(data)
		.filter((key) => {
			// Filter keys for all organizations except AMD
			if (organization === DB_ORG.AMD) return !keysToExclude.includes(key);
			// For AMD, include additional specified fields
			return (
				!keysToExclude.includes(key) ||
				[
					"assetSubArea",
					"propertyId",
					"assetClass",
					"assetNumber",
					"assetRank",
					"assetDescription",
					"assetComment",
					"inServiceDateNotFound",
					"inServiceDate",
				].includes(key)
			);
		})
		.reduce((acc: Record<string, unknown>, key) => {
			acc[key] = data[key];
			return acc;
		}, {});

	//Add refrigeration details
	const asset_refrigeration = {
		refrigerant_type: data.refrigerant ?? null,
		is_refrigerant_not_found: data.refrigerantNotAvailable,
		refrigerant_charge: data.refrigerantCharge ?? null,
		refrigerant_charge_measurement_unit: data.refrigerantChargeUnitMeasurement,
		is_charge_not_found: data.refrigerantChargeNotAvailable,
		usage: data.refUsageType,
	};

	//Add electrical details
	const asset_electrical = {
		amps: data.amps,
		voltage: data.voltage,
		wattage: data.wattage, //calculate with amps * voltage if not found
		is_amps_not_found: data.ampsNotAvailable,
		is_voltage_not_found: data.voltageNotAvailable,
		is_wattage_not_found: data.wattageNotAvailable,
	};

	//Add warranty detail
	const asset_warranty = {
		has_warranty: (data.warrantyStartDate as { base?: string })?.base
			? true
			: null,
		is_warranty_expired: null,
		is_not_found: (data.warrantyStartDate as { base?: string })?.base
			? false
			: null,
		start_date: (data.warrantyStartDate as { base?: string })?.base,
		expiration_date: (data.warrantyExpirationDate as { base?: string })?.base,
		type: null,
		term: data.warrantyPeriod,
		provider: data.warrantyProvider,
	};

	//Add photo details
	const asset_photos = [
		{ type: "ID_TAG", url: data.idTagAssetImage },
		{ type: "TEMP_ALERT", url: data.tempAlertIdAssetImage },
		{ type: "WIDE_ANGLE", url: data.wideAngleAssetImage },
		{ type: "EQUIPMENT", url: data.equipmentAssetImage },
		{ type: "MANUFACTURER_PLATE", url: data.manufacturersPlateAssetImage },
	].filter((photo) => photo.url !== undefined);

	const is_refrigeration = Object.values(asset_refrigeration).some(
		(value) => value !== null && value !== undefined,
	);
	const is_electrical = Object.values(asset_electrical).some(
		(value) => value !== null && value !== undefined,
	);
	const is_warranty = Object.values(asset_warranty).some(
		(value) => value !== null && value !== undefined,
	);

	//Formatting the date
	let isoDate: string | null;
	if ((data?.manufacturerDate as { base?: string })?.base) {
		const date = new Date((data.manufacturerDate as { base: string }).base);
		isoDate = date.toISOString().split("T")[0];
	} else {
		isoDate = null;
	}

	const asset = {
		location_id: data.location,
		organization_asset_type_id: (data.assetType as { id: number })?.id,
		organization_asset_type_name: (data.assetType as { name: string })?.name,
		form_id: parseInt(String(data.formId)), //auto
		submission_id: draftId, //from draft_id
		temp_alert_tag: data.tempAlertId,
		is_temp_alert_not_found: data.tempAlertIdNotAvailable,
		general_notes: data.assetGeneralNotes,
		is_manufacturer_not_found: data.manufacturerNotAvailable,
		is_manufacturer_plate_not_found: data.manufacturerPlateNotAvailable,
		manufacturer_date: isoDate,
		is_manufacturer_date_not_found: data.manufacturerDateNotFound,
		manufacturer_id: (data?.manufacturer as { id?: number })?.id ?? null, //need this for creating a model if it doesnt exist
		m_number: data.modelNumber,
		is_model_not_found: data.modelNotAvailable,
		serial_number: data.serialNumber,
		serial_number_not_found: data.serialNotAvailable,
		condition: data.assetCondition,
		asset_tag: data.assetTag,
		is_refrigeration: is_refrigeration,
		is_electrical: is_electrical,
		is_warranty: is_warranty,
		power_source: data.powerSource,
		organization_equipment_area: data.assetLocation,
		organization_equipment_sub_area:
			organization != DB_ORG.AMD ? data.assetSubArea : null,
		unique_fields: uniques,
		qa_flag: null,
		external_firebase_id: data.externalFirebaseId,
	};

	const body: {
		asset: Record<string, unknown>;
		photos: Array<{ type: string; url: unknown }>;
		raw_input: Record<string, unknown>;
		refrigeration?: Record<string, unknown>;
		electrical?: Record<string, unknown>;
		warranty?: Record<string, unknown>;
	} = {
		asset: asset,
		photos: asset_photos,
		raw_input: data,
	};
	if (is_refrigeration) {
		body.refrigeration = asset_refrigeration;
	}
	if (is_electrical) {
		body.electrical = asset_electrical;
	}
	if (is_warranty) {
		body.warranty = asset_warranty;
	}
	return body;
};

export const insertAssetFromForm = async (
	data: Record<string, unknown>,
	draftId: string | number,
	organization: string,
): Promise<unknown> => {
	try {
		const body = await responseDataToParsedAsset(data, draftId, organization);
		const url = `${
			import.meta.env.VITE_ALFRED_SERVICE_URL
		}/asset/upload/${draftId}`;

		if (!auth.currentUser) {
			throw new Error("User not authenticated");
		}

		const response = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
			},
			body: JSON.stringify(body),
		});
		if (response.status !== 200) {
			const errorDetails = await response.json();
			console.log(errorDetails);
			throw new Error(errorDetails.detail);
		}
		return await response.json();
	} catch (exception) {
		throw new Error(
			exception instanceof Error ? exception.message : String(exception),
		);
	}
};

export const updateAssetFromForm = async (
	data: Record<string, unknown>,
	submissionId: string | number,
	organization: string,
): Promise<unknown> => {
	try {
		const body = await responseDataToParsedAsset(
			data,
			submissionId,
			organization,
		);
		const url = `${
			import.meta.env.VITE_ALFRED_SERVICE_URL
		}/asset/update/${submissionId}`;

		if (!auth.currentUser) {
			throw new Error("User not authenticated");
		}

		const response = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
			},
			body: JSON.stringify(body),
		});
		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to update asset: ${errorDetails?.detail?.[0]?.msg || errorDetails?.detail}`,
			);
		}
		return await response.json();
	} catch (exception) {
		throw new Error(
			`Failed to update asset: ${exception instanceof Error ? exception.message : String(exception)}`,
		);
	}
};

export const getAssetEditHistory = async (
	asset_id: string | number,
): Promise<unknown> => {
	try {
		if (!auth.currentUser) {
			throw new Error("User not authenticated");
		}

		const url = `${
			import.meta.env.VITE_ALFRED_SERVICE_URL
		}/asset/history/${asset_id}`;
		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
			},
		});
		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(errorDetails.detail);
		}
		return await response.json();
	} catch (exception) {
		throw new Error(
			exception instanceof Error ? exception.message : String(exception),
		);
	}
};

export const backfillAssetData = async (
	assetRef: string,
	assetData: Record<string, unknown>,
): Promise<unknown | undefined> => {
	if (!auth.currentUser) {
		throw new Error("User not authenticated");
	}

	const token = await auth.currentUser.getIdToken();
	const SERVICE_ROUTE = import.meta.env.VITE_ALFRED_SERVICE_URL;
	const res = await fetch(`${SERVICE_ROUTE}/backfill-data`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify({ assetRef, asset: assetData }),
	});
	if (res.status !== 200) {
		console.error(`Error backfilling asset data`);
		return undefined;
	} else {
		return await res.json();
	}
};

export const addPredSpendData = async (
	organization: string,
	formId: string | number,
	data: Record<string, unknown>,
	assetRef: { id: string },
	submissionRef: { id: string },
	chosenAssetType: { id: number },
): Promise<void> => {
	const env = import.meta.env.VITE_ENV;
	// Attempt to create asset type relationship
	const response = await fetch(
		env === "production"
			? "https://us-central1-property-echo-4d747.cloudfunctions.net/createAssetTypeRelationship"
			: env === "staging"
				? "https://us-central1-property-echo-staging.cloudfunctions.net/createAssetTypeRelationship"
				: "http://localhost:5001/property-echo-4d747/us-central1/createAssetTypeRelationship",
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				assetId: assetRef.id,
				assetTypeId: chosenAssetType.id,
				orgId: organization,
			}),
		},
	);
	if (response.status !== 200) {
		console.log("error establishing relationship between asset & asset type");
	}
	// Attempt to create master asset relationship
	let masterAsset: { id?: string; avgUsefulLifeYears?: string } | null = null;
	const response2 = await fetch(
		env === "production"
			? "https://us-central1-property-echo-4d747.cloudfunctions.net/createMasterAssetRelationship"
			: env === "staging"
				? "https://us-central1-property-echo-staging.cloudfunctions.net/createMasterAssetRelationship"
				: "http://localhost:5001/property-echo-4d747/us-central1/createMasterAssetRelationship",
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				assetId: assetRef.id,
				assetTypeId: chosenAssetType.id,
				orgId: organization,
			}),
		},
	);
	if (response2.status != 200) {
		console.log("error establishing relationship between asset & master asset");
	} else {
		masterAsset = await response2.json();
	}
	//
	// get missing DOB from firebase functions
	let avgUsefulLifeYears = "";
	if (masterAsset?.avgUsefulLifeYears) {
		avgUsefulLifeYears = masterAsset.avgUsefulLifeYears;
	}
	const response3 = await fetch(
		env === "production"
			? "https://us-central1-property-echo-4d747.cloudfunctions.net/getMissingDOB"
			: env === "staging"
				? "https://us-central1-property-echo-staging.cloudfunctions.net/getMissingDOB"
				: "http://localhost:5001/property-echo-4d747/us-central1/getMissingDOB",
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				assetCondition: data.assetCondition || "",
				avgUsefulLifeYears,
				orgId: organization,
				formId: formId,
				assetId: assetRef.id,
				submissionId: submissionRef.id,
			}),
		},
	);
	if (response3.status != 200) {
		console.log("error establishing getting missing DOB");
	}

	const response4 = await fetch(
		env === "production"
			? "https://us-central1-property-echo-4d747.cloudfunctions.net/addAssetReplacementYear"
			: env === "staging"
				? "https://us-central1-property-echo-staging.cloudfunctions.net/addAssetReplacementYear"
				: "http://localhost:5001/property-echo-4d747/us-central1/addAssetReplacementYear",
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				orgId: organization,
				assetId: assetRef.id,
			}),
		},
	);
	if (response4.status != 200) {
		console.log("error adding replacement year");
	}

	if (masterAsset?.id) {
		console.log(masterAsset);
		const response5 = await fetch(
			env === "production"
				? "https://us-central1-property-echo-4d747.cloudfunctions.net/scrapeReplCost"
				: env === "staging"
					? "https://us-central1-property-echo-staging.cloudfunctions.net/scrapeReplCost"
					: "http://localhost:5001/property-echo-4d747/us-central1/scrapeReplCost",
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					assetId: masterAsset.id,
				}),
			},
		);
		if (response5.status != 200) {
			console.log("Could not scrape replacement cost");
		}
	}
};

export const addAssetHealthScore = async (
	organization: string,
	assetId: string | number,
): Promise<void> => {
	const env = import.meta.env.VITE_ENV;
	const response = await fetch(
		env === "production"
			? "https://us-central1-property-echo-4d747.cloudfunctions.net/getAssetHealthScore"
			: env === "staging"
				? "https://us-central1-property-echo-staging.cloudfunctions.net/getAssetHealthScore"
				: "http://localhost:5001/property-echo-4d747/us-central1/getAssetHealthScore",
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				orgId: organization,
				assetId: assetId,
			}),
		},
	);
	if (response.status !== 200) {
		console.log("error getting healthscore");
	}
};

// TODO: Revist post migration
export const getAllExtractedInfo = async (): Promise<
	QuerySnapshot | undefined
> => {
	try {
		const info = await getDocs(query(collection(db, "asset-specs")));
		return info;
	} catch (e) {
		console.log(e);
		return undefined;
	}
};

export const getExtractedInfo = async (
	model: string,
): Promise<unknown | undefined> => {
	try {
		if (!auth.currentUser) {
			throw new Error("User not authenticated");
		}

		const url = `${
			import.meta.env.VITE_ALFRED_SERVICE_URL
		}/manual-aggregation/${model}`;
		const result = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
			},
		});
		if (result.status !== 200) {
			const errorDetails = await result.json();
			throw new Error(
				`Failed to get extracted info for model: ${model} ${errorDetails.detail}`,
			);
		}
		const responsejson = await result.json();
		return responsejson?.results;
	} catch (e) {
		console.log(`Error getting extracted info for model: ${model}`);
		return undefined;
	}
};

// TODO: Revist post migration
export const getPredSpendAssetCount = async (
	organization: string,
	years?: number,
	locationId?: string,
): Promise<number> => {
	const params: QueryConstraint[] = [where(DB_FILTER.DELETED, "==", false)];
	if (locationId) {
		params.push(where("locationId", "==", locationId));
	}
	if (years) {
		const startYear = new Date().getFullYear() + 1;
		const endYear = startYear + years - 1;
		params.push(where("replacementYear", ">=", startYear));
		params.push(where("replacementYear", "<=", endYear));
	}
	const assetsSnap = await getCountFromServer(
		query(
			collection(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.ASSETS),
			...params,
		),
	);
	return assetsSnap.data().count;
};

// TODO: Revist post migration
export const decommissionAsset = async (
	org: string,
	docId: string,
	decommissionReason: string,
): Promise<void> => {
	if (!docId) {
		throw new Error(
			"Invalid document ID provided. It must be a valid Firestore document ID.",
		);
	}
	const today = new Date();
	const date = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;

	const decommissionData = {
		decommissioned: true,
		decommission_date: date,
		decommission_reason: decommissionReason,
	};

	console.log("Decommissioning asset with ID:", docId);

	await updateDoc(
		doc(db, DB_PATH.ORGANIZATIONS, org, DB_PATH.ASSETS, docId),
		decommissionData,
	);
};

// TODO: Revist post migration
export const getUnAuditedAssets = async (
	organization: string,
	location: string | number,
): Promise<Array<{ id: string; [key: string]: unknown }>> => {
	console.log("Inside getunaudited", organization, location);

	const searchParams = [
		where(DB_FILTER.DELETED, "==", false),
		where(DB_FILTER.OTHER_LOCATION_ID, "==", location),
		where(DB_FILTER.OTHER_AUDITED, "==", false),
		where(DB_FILTER.DECOMMISSIONED, "==", false),
	];

	const responsesSnap = await getDocs(
		query(
			collection(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.ASSETS),
			...searchParams,
		),
	);
	return responsesSnap.docs.map((doc) => ({
		id: doc.id,
		...doc.data(),
	}));
};

// TODO: Revist post migration
export const getAuditedAssets = async (
	organization: string,
	location: string | number,
): Promise<Array<{ id: string; [key: string]: unknown }>> => {
	console.log("Inside getunaudited", organization, location);

	const searchParams = [
		where(DB_FILTER.DELETED, "==", false),
		where(DB_FILTER.OTHER_LOCATION_ID, "==", location),
		where(DB_FILTER.OTHER_AUDITED, "==", true),
		where(DB_FILTER.DECOMMISSIONED, "==", false),
	];

	const responsesSnap = await getDocs(
		query(
			collection(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.ASSETS),
			...searchParams,
		),
	);
	return responsesSnap.docs.map((doc) => ({
		id: doc.id,
		...doc.data(),
	}));
};

export const getDecommissionedAssets = async (
	organization: string,
	location: string | number,
): Promise<Array<{ id: string; [key: string]: unknown }>> => {
	console.log("Inside getunaudited", organization, location);

	const searchParams = [
		where(DB_FILTER.DELETED, "==", false),
		where(DB_FILTER.OTHER_LOCATION_ID, "==", location),
		// where(DB_FILTER.OTHER_AUDITED, '==', false),
		where(DB_FILTER.DECOMMISSIONED, "==", true),
	];

	const responsesSnap = await getDocs(
		query(
			collection(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.ASSETS),
			...searchParams,
		),
	);
	return responsesSnap.docs.map((doc) => ({
		id: doc.id,
		...doc.data(),
	}));
};

export const getDashboardData = async (): Promise<{
	analytics: {
		healthScore: number;
		dollarAmountPastUsefulLife: number;
		totalBudget: number;
	};
	assets: unknown[];
	locations: unknown[];
	projectedSpendingChartData: {
		yearsWithAssets: string[];
		assetTypes: string[];
		assetValuesByYearAndType: Record<string, Record<string, number>>;
		assetCountByYearAndType: Record<string, Record<string, number>>;
		totalBudget: number;
		hasEstimatedDates: boolean;
	};
}> => {
	try {
		const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/dashboard`;

		const token = await auth?.currentUser?.getIdToken();

		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch assets: ${response.statusText}`);
		}

		const res = await response.json();

		if (!res.success) {
			throw new Error(`Failed to fetch assets: ${response.statusText}`);
		}

		return res.data;
	} catch (error: unknown) {
		console.error("Error in getDashboardData:", error);

		const emptyChartData = {
			yearsWithAssets: [],
			assetTypes: [],
			assetValuesByYearAndType: {},
			assetCountByYearAndType: {},
			totalBudget: 0,
			hasEstimatedDates: false,
		};

		return {
			analytics: {
				healthScore: 0,
				dollarAmountPastUsefulLife: 0,
				totalBudget: 0,
			},
			assets: [],
			locations: [],
			projectedSpendingChartData: emptyChartData,
		};
	}
};

/**
 * Get asset counts grouped by type for a specific location.
 * Returns an array sorted descending by count.
 */
export const getAssetTypeSummary = async (
	locationId: number | string,
): Promise<{ typeName: string; count: number }[]> => {
	try {
		const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/dashboard/location/${locationId}/asset-type-summary`;
		const token = await auth?.currentUser?.getIdToken();

		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch asset type summary: ${response.statusText}`);
		}

		const res = await response.json();
		return res.success ? res.data : [];
	} catch (error) {
		console.error("Error fetching asset type summary:", error);
		return [];
	}
};

/**
 * Get assets for a specific location filtered by asset type name.
 */
export const getAssetsByType = async (
	locationId: number | string,
	typeName: string,
): Promise<any[]> => {
	try {
		const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/dashboard/location/${locationId}/assets-by-type?type=${encodeURIComponent(typeName)}`;
		const token = await auth?.currentUser?.getIdToken();

		if (!token) {
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch assets: ${response.statusText}`);
		}

		const res = await response.json();
		return res.success ? res.data : [];
	} catch (error) {
		console.error("Error fetching assets by type:", error);
		return [];
	}
};

// ============================================================================
// Asset Import Functions
// ============================================================================

export interface AssetImportRow {
	location_name?: string;
	location_address?: string;
	location_store_number?: string;
	asset_type?: string;
	asset_tag?: string;
	manufacturer?: string;
	model_number?: string;
	serial_number?: string;
	condition?: string;
	date_of_birth?: string;
	manufacturer_date?: string;
	general_notes?: string;
	power_source?: string;
	equipment_area?: string;
}

/**
 * Import a single asset from spreadsheet data.
 * Resolves location name, asset type, and manufacturer to IDs on the backend.
 */
export const importAsset = async (row: AssetImportRow): Promise<{ assetId: number }> => {
	const url = `${VITE_CORE_API_URL}/core/asset-import`;
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
		body: JSON.stringify(row),
	});

	const data = await response.json();
	if (!response.ok || !data.success) {
		throw new Error(data.message || "Failed to import asset");
	}
	return data.data;
};

/**
 * Fetch reference data (available asset types, location names) for import mapping validation.
 */
export const getImportReferenceData = async (): Promise<{
	assetTypes: string[];
	locations: string[];
}> => {
	const url = `${VITE_CORE_API_URL}/core/asset-import/reference-data`;
	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	const data = await response.json();
	if (!response.ok || !data.success) {
		throw new Error(data.message || "Failed to fetch reference data");
	}
	return data.data;
};
